import React, { useState } from 'react';
import { Download, FileText, Loader2 } from 'lucide-react';
import DateRangeFilter from '@/components/DateRangeFilter';
import { exportAPI } from '@/lib/api';
import { toast } from 'sonner';

const todayStr = () => new Date().toISOString().split('T')[0];
const monthStartStr = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
};

const FILE_LIST = [
  { label: 'Sales (date-filtered)',              key: 'sales' },
  { label: 'Purchases (date-filtered)',          key: 'purchases' },
  { label: 'Khata Transactions (date-filtered)', key: 'khata' },
  { label: 'Current Inventory (snapshot)',       key: 'inventory' },
  { label: 'Customers (snapshot)',               key: 'customers' },
  { label: 'Stock Master (snapshot)',            key: 'stock_master' },
  { label: 'Suppliers (snapshot)',               key: 'suppliers' },
];

const ALL_KEYS = FILE_LIST.map(f => f.key);

const ExportDataTab = () => {
  const [dateRange, setDateRange] = useState({ start: monthStartStr(), end: todayStr() });
  const [selected, setSelected] = useState(new Set(ALL_KEYS));
  const [loading, setLoading] = useState(false);

  const toggle = (key) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const allChecked = selected.size === ALL_KEYS.length;
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(ALL_KEYS));

  const handleDownload = async () => {
    if (!dateRange.start || !dateRange.end) {
      toast.error('Please select a valid date range.');
      return;
    }
    if (dateRange.start > dateRange.end) {
      toast.error('Start date must be before end date.');
      return;
    }
    if (selected.size === 0) {
      toast.error('Select at least one file to download.');
      return;
    }
    setLoading(true);
    try {
      const blob = await exportAPI.downloadZip(dateRange.start, dateRange.end, [...selected]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kiranaflow_export_${dateRange.start}_${dateRange.end}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded successfully.');
    } catch (err) {
      toast.error(err.message || 'Export failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const s = dateRange.start;
  const e = dateRange.end;
  const today = todayStr();

  const fileNames = {
    sales:        `sales_${s}_${e}.csv`,
    purchases:    `purchases_${s}_${e}.csv`,
    khata:        `khata_transactions_${s}_${e}.csv`,
    inventory:    `inventory_${today}.csv`,
    customers:    `customers_${today}.csv`,
    stock_master: `stock_master_${today}.csv`,
    suppliers:    `suppliers_${today}.csv`,
  };

  return (
    <div className="space-y-5">
      {/* Description */}
      <div className="rounded-xl border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
        Download selected store data as a ZIP of CSV files — ready to open in Excel or Google Sheets.
        This export covers raw transactional and master data; it is not a generated report.
        Snapshot files reflect current state at the time of download.
      </div>

      {/* Two-column layout: controls left, file checklist right */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: date range + button */}
        <div className="space-y-5">
          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">Date range for sales, purchases &amp; khata</p>
            <DateRangeFilter dateRange={dateRange} onChange={setDateRange} />
          </div>

          <button
            onClick={handleDownload}
            disabled={loading || selected.size === 0}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium transition-opacity disabled:opacity-60 hover:opacity-90"
          >
            {loading
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Download className="h-4 w-4" />
            }
            {loading
              ? 'Preparing download…'
              : selected.size === ALL_KEYS.length
                ? 'Download all as ZIP'
                : `Download ${selected.size} file${selected.size !== 1 ? 's' : ''} as ZIP`
            }
          </button>
        </div>

        {/* Right: file checklist */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-foreground">Files to include in the ZIP</p>
            <button
              onClick={toggleAll}
              className="text-xs text-primary hover:underline"
            >
              {allChecked ? 'Deselect all' : 'Select all'}
            </button>
          </div>
          <div className="rounded-xl border border-border divide-y divide-border">
            {FILE_LIST.map(({ label, key }) => {
              const checked = selected.has(key);
              return (
                <label
                  key={key}
                  className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-muted/40 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(key)}
                    className="h-4 w-4 rounded border-border accent-primary shrink-0"
                  />
                  <FileText className={`h-4 w-4 shrink-0 ${checked ? 'text-primary' : 'text-muted-foreground'}`} />
                  <div className="min-w-0 flex-1">
                    <p className={`text-xs font-mono truncate ${checked ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {fileNames[key]}
                    </p>
                    <p className="text-xs text-muted-foreground">{label}</p>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportDataTab;
